<script setup lang="ts" generic="TData, TValue">
import { opChain } from '@collector/shared-utils'
import {
  FlexRender,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
  type ColumnDef,
  type RowData,
  type TableOptions,
} from '@tanstack/vue-table'

import Table from '../Table/Table.vue'
import TBody from '../Table/TBody.vue'
import TBodyColumn from '../Table/TBodyColumn.vue'
import THead from '../Table/THead.vue'
import THeadColumn from '../Table/THeadColumn.vue'
import TRow from '../Table/TRow.vue'

type Props = {
  columns: ColumnDef<TData, TValue>[]
  data?: TData[]
  options?: Partial<TableOptions<TData>>
  highlightedRowIds?: string[]
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ColumnMeta<TData extends RowData, TValue> {
  sticky?: string
  align?: 'start' | 'end'
}

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  highlightedRowIds: () => [],
})

// composables
const table = useVueTable({
  get data() {
    return props.data
  },
  get columns() {
    return props.columns
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  ...props.options,
})

// methods
function getCellMetaClass(meta?: ColumnMeta<TData, TValue>): string {
  const result: string[] = []
  if (meta?.sticky) result.push('sticky', meta.sticky)
  if (meta?.align === 'end') result.push('text-end')
  else if (meta?.align === 'start') result.push('text-start')
  return result.join(' ')
}
</script>

<template>
  <div class="relative w-full overflow-auto rounded-md">
    <Table>
      <THead>
        <TRow
          v-for="headerGroup in table.getHeaderGroups()"
          :key="headerGroup.id"
        >
          <THeadColumn
            v-for="header in headerGroup.headers"
            :key="header.id"
            :class="[
              getCellMetaClass(header.column.columnDef.meta),
              { 'cursor-pointer': header.column.columnDef.enableSorting },
            ]"
            @click="header.column.toggleSorting()"
          >
            <FlexRender
              v-if="!header.isPlaceholder"
              :render="header.column.columnDef.header"
              :props="header.getContext()"
            />
          </THeadColumn>
        </TRow>
      </THead>

      <TBody>
        <template v-if="opChain(table.getRowModel().rows, (v) => v.length)">
          <TRow
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :highlighted="props.highlightedRowIds.includes(row.id)"
            :data-state="row.getIsSelected() ? 'selected' : undefined"
          >
            <TBodyColumn
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              :class="getCellMetaClass(cell.column.columnDef.meta)"
            >
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </TBodyColumn>
          </TRow>
        </template>

        <template v-else>
          <TRow>
            <TBodyColumn
              class="h-24 text-center"
              :colspan="columns.length"
            >
              No results
            </TBodyColumn>
          </TRow>
        </template>
      </TBody>
    </Table>
  </div>
</template>

<style scoped>
td:nth-last-child(1 of .sticky),
th:nth-last-child(1 of .sticky) {
  @apply border-r-1;
}
</style>
